import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import {
    tg,
    verifyInitDataOnServer,
    applyTelegramTheme,
    subscribeOnThemeChange,
    unsubscribeOnThemeChange,
    expandApp,
    enableClosingConfirmation,
    disableClosingConfirmation,
} from './services/telegram';

import './styles/global.scss';

const LazyLogin = React.lazy(() => import('./pages/Login'));
const LazyRegistrationStep1 = React.lazy(() => import('./pages/RegistrationStep1'));
const LazyRegistrationStep2 = React.lazy(() => import('./pages/RegistrationStep2'));
const LazyRegistrationStep3 = React.lazy(() => import('./pages/RegistrationStep3'));
const LazyRegistrationStep4 = React.lazy(() => import('./pages/RegistrationStep4'));
const LazyForgotPasswordStep1 = React.lazy(() => import('./pages/ForgotPasswordStep1'));
const LazyForgotPasswordStep2 = React.lazy(() => import('./pages/ForgotPasswordStep2'));
const LazyForgotPasswordStep3 = React.lazy(() => import('./pages/ForgotPasswordStep3'));
const LazyMainPage = React.lazy(() => import('./pages/MainPage'));
const LazyProfile = React.lazy(() => import('./pages/Profile'));

const App: React.FC = () => {
    const [initDataVerified, setInitDataVerified] = useState<boolean | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (tg) {
            tg.ready();
            verifyInitData().catch(console.error);
            applyTelegramTheme();
            subscribeOnThemeChange();
            expandApp();
            enableClosingConfirmation();
        } else {
            setInitDataVerified(false);
            setError('Запуск вне Telegram WebApp');
            console.error("ERROR: Telegram WebApp не определён");
        }
        return () => {
            unsubscribeOnThemeChange();
            disableClosingConfirmation();
        };
    }, []);

    useEffect(() => {
        if (tg) {
            fetch('/api/telegram/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ initData: tg.initData })
            })
                .then(res => res.json())
                .then(data => {
                    if (data.result) {
                        setInitDataVerified(true);
                    } else {
                        setInitDataVerified(false);
                        setError(data.errorText || 'Ошибка верификации initData');
                    }
                })
                .catch(err => {
                    console.error("ERROR: Запрос /api/telegram/verify завершился ошибкой:", err);
                    setInitDataVerified(false);
                    setError("Request error");
                });
        } else {
            console.error("ERROR: Telegram WebApp не определён – убедитесь, что скрипт telegram-web-app.js подключён");
        }
    }, []);

    async function verifyInitData() {
        const resp = await verifyInitDataOnServer();
        if (resp.result) {
            setInitDataVerified(true);
        } else {
            setInitDataVerified(false);
            setError(resp.errorText || 'Ошибка верификации initData');
        }
    }

    if (initDataVerified === null) {
        return <div style={{ textAlign: 'center', marginTop: 40 }}>Загрузка...</div>;
    }
    if (initDataVerified === false) {
        return (
            <div style={{ padding: 20, textAlign: 'center', color: 'red' }}>
                <p>Ошибка проверки initData:</p>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <Suspense fallback={<div style={{ textAlign: 'center', marginTop: 40 }}>Загрузка модуля...</div>}>
            <Router>
                <Routes>
                    <Route path="/" element={<LazyLogin />} />
                    <Route path="/registration/step1" element={<LazyRegistrationStep1 />} />
                    <Route path="/registration/step2" element={<LazyRegistrationStep2 />} />
                    <Route path="/registration/step3" element={<LazyRegistrationStep3 />} />
                    <Route path="/registration/step4" element={<LazyRegistrationStep4 />} />
                    <Route path="/forgot-password-step1" element={<LazyForgotPasswordStep1 />} />
                    <Route path="/forgot-password-step2" element={<LazyForgotPasswordStep2 />} />
                    <Route path="/forgot-password-step3" element={<LazyForgotPasswordStep3 />} />
                    <Route path="/main" element={<LazyMainPage />} />
                    <Route path="/profile" element={<LazyProfile />} />
                </Routes>
            </Router>
        </Suspense>
    );
};

export default App;